/* ═══════════════════════════════════════════════════════════════════════
   SUSUN HASIL GENERATE → PROMPT KAD (dipindah VERBATIM dari WorkspaceView
   generateForProduct, 2026-10-26).
   Satu salinan, dua pemanggil:
     1. generateForProduct — page masih terbuka bila server pulangkan hasil.
     2. Laluan "hasil tertinggal" (hooks/useHasilGenerateTertinggal.js) — page
        ditutup / dimuat semula semasa Generate, hasil diambil dari peti progress.
   ⛔ Jangan salin logik ini ke tempat lain — ubah di sini sahaja.
   ═══════════════════════════════════════════════════════════════════════ */
import { resolveMandatoryAppend } from './promptResolver';
import { tanpaCaptionFbSendiri } from './captionKad';

/**
 * @param {object} p
 * @param {object} p.current         kad dalam state (prompts sedia ada dikekalkan untuk set yang tiada dalam hasil)
 * @param {object} p.combinedResult  objek dipulangkan server (set1/set2/set3 + _intelligenceData)
 * @param {object} p.logic           { mandatory*Append, prependScene2* } — module atau logic lama
 * @param {object} p.appendProduct   produk untuk placeholder ayat wajib ({{pronoun}}, {{gender}})
 * @param {object|null} p.appendAvatar avatar untuk placeholder ayat wajib
 * @param {object|null} p.intelSimpanan intel dari cache detail (Split V2) bila state ringan tiada intel
 * @returns {{ newPrompts: object, rawOutput: string, intelligenceToSave: object|null }}
 */
export function susunHasilGenerate({ current, combinedResult, logic, appendProduct, appendAvatar = null, intelSimpanan = null }) {
        const newPrompts = { ...current.prompts };

        // Get mandatory append text from the logic option.
        // Resolve placeholders (e.g. {{pronoun}}, {{gender}}) so Veo/Gemini gets
        // gender-correct text. These don't go to OpenAI → zero token cost.
        const mandatoryVideoAppend = resolveMandatoryAppend(logic.mandatoryVideoAppend || '', appendProduct, appendAvatar);
        const mandatoryVideoAppend2 = resolveMandatoryAppend(logic.mandatoryVideoAppend2 || '', appendProduct, appendAvatar);
        const mandatoryImageAppend = resolveMandatoryAppend(logic.mandatoryImageAppend || '', appendProduct, appendAvatar);
        console.log('[generateForProduct] ★★ MANDATORY APPEND CHECK:', {
            videoAppendLength: mandatoryVideoAppend.length,
            imageAppendLength: mandatoryImageAppend.length,
            videoAppendPreview: mandatoryVideoAppend.substring(0, 50),
            imageAppendPreview: mandatoryImageAppend.substring(0, 50),
            resultKeys: Object.keys(combinedResult)
        });
        // ★ DEBUG: confirm _templateTrace received from Cloud Function
        console.log('[generateForProduct] 🔎 TEMPLATE TRACE from server:', combinedResult?._templateTrace);

        Object.keys(combinedResult).forEach(setKey => {
            if (setKey === '_intelligenceData') return; // skip — not a prompt set
            const setData = combinedResult[setKey];
            if (setData && typeof setData === 'object') {
                // ★ TEMPLATE MODE (FIX bug edit hook/script): kerja atas TEMPLATE (ada placeholder),
                //   append/prepend admin ke template, lepas tu DERIVE versi substituted supaya
                //   display == template + nilai. Produk dari Cloud Function baru ada *Template;
                //   response lama (tanpa *Template) fallback ke substituted → laluan legacy.
                let imagePromptTpl = setData.promptImageTemplate ?? (setData.promptImage || '');
                let videoPromptTpl = setData.promptVideoTemplate ?? (setData.promptVideo || '');
                let videoPrompt1Tpl = setData.promptVideoScene1Template ?? (setData.promptVideoScene1 || '');
                let videoPrompt2Tpl = setData.promptVideoScene2Template ?? (setData.promptVideoScene2 || '');
                const hookTextVal = setData.hookText || '';
                const videoScriptVal = setData.videoScript || '';
                const videoScript1Val = setData.videoScript1 || '';
                const videoScript2Val = setData.videoScript2 || '';

                // ★ Scene 2 PREPEND — admin-controlled (2-scene sahaja). Append ke TEMPLATE
                //   (teks prepend takda placeholder, selamat). Veo abaikan arahan tone/pace kalau
                //   letak di hujung (skrip jadi slow) — suntik di ATAS dulu.
                if (logic.prependScene2Enabled && logic.prependScene2Text && videoPrompt2Tpl) {
                    const prependText = resolveMandatoryAppend(logic.prependScene2Text, appendProduct, appendAvatar);
                    if (prependText) videoPrompt2Tpl = prependText.trim() + '\n\n' + videoPrompt2Tpl.trim();
                }
                // Auto-append mandatory text ke video template(s)
                if (mandatoryVideoAppend) {
                    if (videoPromptTpl) videoPromptTpl = videoPromptTpl.trim() + '\n\n' + mandatoryVideoAppend.trim();
                    if (videoPrompt1Tpl) videoPrompt1Tpl = videoPrompt1Tpl.trim() + '\n\n' + mandatoryVideoAppend.trim();
                    // Scene2 guna append sendiri kalau ada, else fallback ke scene1 append
                    if (videoPrompt2Tpl) videoPrompt2Tpl = videoPrompt2Tpl.trim() + '\n\n' + (mandatoryVideoAppend2 ? mandatoryVideoAppend2.trim() : mandatoryVideoAppend.trim());
                } else if (mandatoryVideoAppend2 && videoPrompt2Tpl) {
                    videoPrompt2Tpl = videoPrompt2Tpl.trim() + '\n\n' + mandatoryVideoAppend2.trim();
                }
                // Auto-append mandatory text ke image template
                if (mandatoryImageAppend && imagePromptTpl) {
                    imagePromptTpl = imagePromptTpl.trim() + '\n\n' + mandatoryImageAppend.trim();
                }

                // Derive versi substituted dari template (display + flow input = template+nilai).
                const _subst = (tpl, tokens) => {
                    let o = tpl || '';
                    for (const [tok, val] of Object.entries(tokens)) { if (val) o = o.split(tok).join(val); }
                    return o;
                };
                const imagePrompt = _subst(imagePromptTpl, { '[HOOK]': hookTextVal });
                const videoPrompt = _subst(videoPromptTpl, { '[SCRIPT]': videoScriptVal, '[HOOK]': hookTextVal });
                const videoPrompt1 = _subst(videoPrompt1Tpl, { '[SCRIPT1]': videoScript1Val, '[HOOK]': hookTextVal });
                const videoPrompt2 = _subst(videoPrompt2Tpl, { '[SCRIPT2]': videoScript2Val, '[HOOK]': hookTextVal });

                // ★ N-SCENE (babak 3+) — additive, scene1/2 di atas tak disentuh.
                // Babak 3+ guna append "continuation" (mandatoryVideoAppend2 kalau ada, else mandatoryVideoAppend).
                const extraSceneFields = {};
                // ★ RESIPI gambar berlainan: Image Prompt scene 2+ (scene 1 = promptImage di atas).
                for (let _sc = 2; _sc <= 6; _sc++) {
                    let _tpl = setData[`promptImageScene${_sc}`] || '';
                    if (!_tpl) continue;
                    if (mandatoryImageAppend) _tpl = _tpl.trim() + '\n\n' + mandatoryImageAppend.trim();
                    extraSceneFields[`promptImage${_sc}`] = _subst(_tpl, { '[HOOK]': hookTextVal });
                }
                for (let _sc = 3; _sc <= 6; _sc++) {
                    let _tpl = setData[`promptVideoScene${_sc}Template`] ?? (setData[`promptVideoScene${_sc}`] || '');
                    if (!_tpl) continue;
                    const _scrVal = setData[`videoScript${_sc}`] || '';
                    const _contAppend = mandatoryVideoAppend2 ? mandatoryVideoAppend2.trim() : (mandatoryVideoAppend ? mandatoryVideoAppend.trim() : '');
                    if (_contAppend) _tpl = _tpl.trim() + '\n\n' + _contAppend;
                    const _vp = _subst(_tpl, { [`[SCRIPT${_sc}]`]: _scrVal, '[HOOK]': hookTextVal });
                    extraSceneFields[`promptVideo${_sc}`] = _vp;
                    if (setData[`promptVideoScene${_sc}Template`] != null) extraSceneFields[`promptVideo${_sc}Template`] = _tpl;
                    if (_scrVal) extraSceneFields[`videoScript${_sc}`] = _scrVal;
                }

                newPrompts[setKey] = {
                    /* ★ [2026-09-27] Keputusan user: caption TikTok dijana semula → caption FB
                       yang pernah diubah (atau dikosongkan) DIBUANG, FB kembali ikut caption
                       TikTok baharu. Laluan Agent Saya (gantiPromptsDenganHasil) + Retry sudah
                       mengosongkannya; laluan ini dahulu membawa caption FB lama (spread). */
                    ...tanpaCaptionFbSendiri(newPrompts[setKey]),
                    promptImage: imagePrompt,
                    // ★ hookText separate field — pair dengan [HOOK] placeholder dalam template.
                    hookText: hookTextVal,
                    promptVideo: videoPrompt,
                    // ★ videoScript single-scene (GOYANG, AI8SAAT) — pair dengan [SCRIPT] placeholder.
                    videoScript: videoScriptVal,
                    captionTiktok: setData.captionTiktok || '',
                    // ★ Template fields (FIX) — hanya kalau Cloud Function hantar template.
                    //   Kehadiran field ni = produk guna sistem template baru (sync deterministik).
                    ...(setData.promptImageTemplate != null ? { promptImageTemplate: imagePromptTpl } : {}),
                    ...(setData.promptVideoTemplate != null ? { promptVideoTemplate: videoPromptTpl } : {}),
                    // AI16SAAT/Home Niche 2-scene fields (empty for single-scene — no impact)
                    ...(videoPrompt1 ? { promptVideo1: videoPrompt1 } : {}),
                    ...(videoPrompt2 ? { promptVideo2: videoPrompt2 } : {}),
                    ...(videoPrompt1 && setData.promptVideoScene1Template != null ? { promptVideo1Template: videoPrompt1Tpl } : {}),
                    ...(videoPrompt2 && setData.promptVideoScene2Template != null ? { promptVideo2Template: videoPrompt2Tpl } : {}),
                    // ★ videoScript1/videoScript2 pair dengan [SCRIPT1]/[SCRIPT2] placeholders (2-scene).
                    ...(videoScript1Val ? { videoScript1: videoScript1Val } : {}),
                    ...(videoScript2Val ? { videoScript2: videoScript2Val } : {}),
                    // ★ N-SCENE babak 3+ (promptVideo3.., promptVideo3Template.., videoScript3..)
                    ...extraSceneFields
                };
            }
        });

        // Store raw output (exclude _intelligenceData — saved separately on product)
        const { _intelligenceData: newIntelligence, ...resultForStorage } = combinedResult || {};
        const rawOutput = JSON.stringify(resultForStorage, null, 2);

        // Persist intelligence data from this run (or keep existing if reusing cached)
        // ★ SPLIT V2: state ringan mungkin takda intel — fallback ke detailCache
        const intelligenceToSave = newIntelligence || current.intelligenceData
            || intelSimpanan || null;
        if (newIntelligence) {
            console.log(`[generateForProduct] ★ Auto-saving Intelligence data for "${current.name}"`);
        }

    return { newPrompts, rawOutput, intelligenceToSave };
}

/** Objek `logic` untuk produk yang ada module (master prompt diselesaikan di server).
 *  Dipindah VERBATIM dari generateForProduct — pemanggil sama seperti susunHasilGenerate. */
export function logikDariModule(matchedModule, productModuleId) {
    return {
        id: `module_${productModuleId}`,
        name: matchedModule.name,
        jsonMode: matchedModule.jsonMode !== false, // default true
        // combinedInstruction intentionally omitted — server resolves from Firestore
        mandatoryImageAppend: matchedModule.mandatoryImageAppend || '',
        mandatoryVideoAppend: matchedModule.mandatoryVideoAppend || '',
        mandatoryVideoAppend2: matchedModule.mandatoryVideoAppend2 || '',
        // Scene 2 prepend (admin-controlled — Veo abaikan arahan kat hujung)
        prependScene2Enabled: matchedModule.prependScene2Enabled || false,
        prependScene2Text: matchedModule.prependScene2Text || ''
    };
}
